import {
  Image,
  Video,
  Music,
  FileText,
  Sheet,
  Presentation,
  Archive,
  Code,
  File
} from 'lucide-react'

interface MimeTypeIconProps {
  mimeType: string
  extension?: string | null
}

interface MimeTypeCategory {
  color: string
  icon: React.ComponentType<{ className?: string }>
}

export default function MimeTypeIcon({ mimeType, extension }: MimeTypeIconProps) {
  const getCategoryInfo = (mime: string, ext?: string | null): MimeTypeCategory => {
    // Images
    if (mime.startsWith('image/')) {
      return { color: 'text-info', icon: Image }
    }

    // Videos
    if (mime.startsWith('video/')) {
      return { color: 'text-secondary', icon: Video }
    }

    // Audio
    if (mime.startsWith('audio/')) {
      return { color: 'text-accent', icon: Music }
    }

    // PDFs
    if (mime === 'application/pdf') {
      return { color: 'text-error', icon: FileText }
    }

    // Documents
    if (
      mime.includes('word') ||
      mime.includes('document') ||
      mime === 'application/vnd.oasis.opendocument.text' ||
      ext === 'doc' ||
      ext === 'docx' ||
      ext === 'odt'
    ) {
      return { color: 'text-primary', icon: FileText }
    }

    // Spreadsheets
    if (
      mime.includes('sheet') ||
      mime.includes('excel') ||
      mime === 'application/vnd.oasis.opendocument.spreadsheet' ||
      ext === 'xls' ||
      ext === 'xlsx' ||
      ext === 'ods'
    ) {
      return { color: 'text-success', icon: Sheet }
    }

    // Presentations
    if (
      mime.includes('presentation') ||
      mime.includes('powerpoint') ||
      mime === 'application/vnd.oasis.opendocument.presentation' ||
      ext === 'ppt' ||
      ext === 'pptx' ||
      ext === 'odp'
    ) {
      return { color: 'text-warning', icon: Presentation }
    }

    // Archives
    if (
      mime.includes('zip') ||
      mime.includes('compressed') ||
      mime.includes('archive') ||
      ext === 'zip' ||
      ext === 'rar' ||
      ext === '7z' ||
      ext === 'tar' ||
      ext === 'gz'
    ) {
      return { color: 'text-neutral', icon: Archive }
    }

    // Code files
    if (
      mime.includes('json') ||
      mime.includes('javascript') ||
      mime.includes('xml') ||
      mime.includes('html') ||
      mime === 'text/plain' ||
      ext === 'js' ||
      ext === 'ts' ||
      ext === 'jsx' ||
      ext === 'tsx' ||
      ext === 'py' ||
      ext === 'rb' ||
      ext === 'php' ||
      ext === 'java' ||
      ext === 'c' ||
      ext === 'cpp' ||
      ext === 'css' ||
      ext === 'scss'
    ) {
      return { color: 'text-base-content/50', icon: Code }
    }

    // Default
    return { color: 'text-base-content/50', icon: File }
  }

  const { color, icon: Icon } = getCategoryInfo(mimeType, extension)

  return (
    <Icon className={`w-6 h-6 ${color}`} />
  )
}
